import { Injectable, OnDestroy, inject } from '@angular/core';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Logger } from '@df/core/service/logger';
import { TimeoutService } from '@df/ng/timeout.service';
import { CustomerModel } from '@df/session/api/customer.model';
import each from 'lodash-es/each';
import chunk from 'lodash-es/chunk';
import { BehaviorSubject, Subscription, merge } from 'rxjs';
import { TradeService } from './../trade.service';
import { app } from '@df/core/app';
import { ITierPrice } from 'projects/app-default/src/app/catalog/product/product.decorator';

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            has_custom_agreements?: boolean;
            customer_trade_account?: string;
        }
    }
}

enum EProductEntryStatus {
    nodata,
    pending,
    loading,
    ready,
    error
}

interface IProductEntryPrice {
    price: number;
    orgPrice?: number;
    groupPrice?: {
        [key: number]: number;
    };
    groupOrgPrice?: {
        [key: number]: number | false;
    };
    tierPrices?: ITierPrice[];
}

interface IProductEntry {
    product: Product;
    promise: Promise<void>;
    price$: BehaviorSubject<IProductEntryPrice | undefined>;
    canDisplay$: BehaviorSubject<boolean>;
    resolver: () => void;
    dataSubscription: Subscription;
    status: EProductEntryStatus;
}

type IPricingResponse = Record<
    string,
    {
        AdditionalDiscountPercentage1: number;
        AdditionalDiscountPercentage2: number;
        AdditionalDiscountPercentage3: number;
        BaseGrossPrice: number;
        BaseNetPrice: number;
        DiscountPercentage: number;
        EMCAmount: number;
        GrossPrice: number;
        NetPrice: number;
        QuantityBreakInfos: {
            Quantity: number;
            NetPrice: number;
            GrossPrice: number;
            CalculationFactor: number;
            CalculationMethod: string;
            QuantityBreakTableID: string;
        }[];
    }
>;

declare module '@df/core/app' {
    interface App {
        tradePricingService: TradePricingService;
    }
}
declare module '@df/catalog/product/product' {
    interface IProductDataV1 {
        _nativePrice?: IProductEntryPrice;
    }
}

@Injectable({
    providedIn: 'root'
})
export class TradePricingService implements OnDestroy {
    /**
     * Ids that are already processed
     */
    private productEntries = new Map<number, IProductEntry>();

    /**
     * Map sku => product id
     */
    private skuMap = new Map<string, number>();

    /**
     * Prices for given products
     */
    private loadedPrices = new Map<number, { price: number; tierPrices?: ITierPrice[] } | undefined>();

    /**
     * Change subscription
     */
    private subscriptions = new Subscription();

    /**
     * Product resource
     */
    private productResource = inject(ProductResource);

    /**
     * Timeout service
     */
    private timeoutService = inject(TimeoutService);

    /**
     * Customer model
     */
    private customerModel = inject(CustomerModel);

    /**
     * Trade service
     */
    private tradeService = inject(TradeService);

    /**
     * Logger service
     */
    private loggerService = inject(Logger);

    /**
     * Current data customer id
     */
    private customerId?: string;

    /**
     * Is trade customer flag
     */
    private isTradeCustomer = false;

    /**
     * Customer loaded status
     */
    private customerLoaded = false;

    /**
     * ALways try behavior subject
     */
    private alwayTrue$ = new BehaviorSubject<boolean>(true);

    constructor() {
        app.tradePricingService = this;
        this.productResource.addPlugin(model => this.createProductEntry(model));
        this.subscriptions.add(this.customerModel.obs.data.subscribe(() => this.onCustomerDataChange()));
        if (this.customerModel.loaded) {
            this.onCustomerDataChange();
        }
    }

    /**
     * Gets information about product price display
     */
    getCanDisplay$(products: Product[]): BehaviorSubject<boolean> {
        products = products.filter(product => this.supportsTradePrice(product));
        if (products.length === 0) {
            return this.alwayTrue$;
        }
        if (products.length === 1) {
            return this.getCreateEntry(products[0])!.canDisplay$;
        }
        return this.createCanDisplay$(products);
    }

    getPrice$(product: Product): BehaviorSubject<IProductEntryPrice | undefined> | undefined {
        return this.getCreateEntry(product)?.price$;
    }

    /**
     * Creates multi product display check
     */
    private createCanDisplay$(products: Product[]): BehaviorSubject<boolean> {
        const canDisplay$ = new BehaviorSubject<boolean>(false);
        const entries = products.map(product => this.getCreateEntry(product)).filter(entry => !!entry) as IProductEntry[];
        const subscriptions = entries.map(entry =>
            entry.canDisplay$.subscribe(_canDisplay => {
                const canDisplay = _canDisplay && entries.every(entry => entry.canDisplay$.value);
                if (canDisplay$.value !== canDisplay) {
                    canDisplay$.next(canDisplay);
                }
            })
        );

        const uns = canDisplay$.unsubscribe;
        canDisplay$.unsubscribe = () => {
            subscriptions.forEach(sub => sub.unsubscribe());
            uns;
        };

        return canDisplay$;
    }

    /**
     * Creates new entry for given product
     */
    private createProductEntry(product: Product) {
        if (!this.supportsTradePrice(product)) {
            return;
        }
        this.clear(product.id);
        const price$ = new BehaviorSubject<IProductEntryPrice | undefined>(this.getProductPrice(product, false));

        const entry: Partial<IProductEntry> = {
            product,
            price$,
            canDisplay$: new BehaviorSubject<boolean>(this.canDisplayPrice(product)),
            dataSubscription: merge(product.change, price$).subscribe(() => this.syncProductData(product)),
            status: product.data ? EProductEntryStatus.pending : EProductEntryStatus.nodata
        };

        entry.promise = new Promise<void>(resolver => {
            entry.resolver = resolver;
            this.productEntries.set(product.id, entry as IProductEntry);
        });

        this.applyPrice(product);
        this.loadMissing();
    }

    /**
     * Retrieves an entry making sure it exists
     */
    private getCreateEntry(product: Product): IProductEntry | undefined {
        if (!this.productEntries.has(product.id)) {
            this.createProductEntry(product);
        }
        return this.productEntries.get(product.id)!;
    }

    /**
     * If price can be displayed check
     */
    private canDisplayPrice(product: Product) {
        if (!this.customerModel.loaded) {
            return false;
        }

        if (!this.customerId) {
            return !!product.data?._nativePrice;
        }

        const status = this.productEntries.get(product.id)?.status;
        return status !== undefined && [EProductEntryStatus.ready, EProductEntryStatus.error].includes(status);
    }

    /**
     * If customer is changed then reset all entries
     */
    private onCustomerDataChange() {
        const isTradeCustomer = this.tradeService.isTradeCustomer();
        const customerId =
            isTradeCustomer && this.customerModel.data?.has_custom_agreements ? this.customerModel.data?.customer_trade_account : undefined;

        if (this.customerId !== customerId) {
            this.loadedPrices.clear();
        }

        if (
            this.customerId !== customerId ||
            this.customerLoaded !== this.customerModel.loaded ||
            this.isTradeCustomer !== isTradeCustomer
        ) {
            this.customerId = customerId;
            this.isTradeCustomer = isTradeCustomer;
            this.customerLoaded = this.customerModel.loaded;
            this.productEntries.forEach((entry, id) => this.resetEntry(id));
        }
    }

    /**
     * Reset entry to pending state, revert any product data changes
     */
    private resetEntry(id: number) {
        const entry = this.productEntries.get(id);
        if (entry) {
            const productData = entry.product.data;
            if (productData) {
                entry.status = EProductEntryStatus.pending;
                this.setPriceValue(entry.product, this.getProductPrice(entry.product, true));
                entry.promise = new Promise<void>(resolver => (entry.resolver = resolver));
            } else {
                entry.status = EProductEntryStatus.nodata;
            }
            this.applyPrice(entry.product);
            this.loadMissing();
        }
    }

    /**
     * Sets entry price$ value
     */
    private setPriceValue(product: Product, entryPrice: IProductEntryPrice | undefined) {
        const entry = this.productEntries.get(product.id);
        if (entry) {
            const value = entry.price$.value;
            if (value && entryPrice ? value.price !== entryPrice.price || value.orgPrice !== entryPrice.orgPrice : value || entryPrice) {
                entry.price$.next(entryPrice);
            }
        }
    }

    /**
     * Removes service data for given entry
     */
    private clear(id: number) {
        const entry = this.productEntries.get(id);
        if (entry) {
            entry.dataSubscription?.unsubscribe();
            this.productEntries.delete(id);
        }
    }

    /**
     * Syncs product price with entry
     */
    private syncProductData(product: Product) {
        this.applyPrice(product);
        if (!this.supportsTradePrice(product)) {
            this.clear(product.id);
            return;
        }
        const entry = this.productEntries.get(product.id);
        const data = product.data;

        if (entry) {
            if (data) {
                if (entry.status === EProductEntryStatus.nodata) {
                    entry.status = EProductEntryStatus.pending;
                }
                if (entry.status === EProductEntryStatus.pending) {
                    this.loadMissing();
                }
            } else {
                entry.status = EProductEntryStatus.nodata;
            }
        }
    }

    /**
     * Loads missing prices
     */
    private loadMissing = this.timeoutService.debounce(async () => {
        // do nothing until we have a customer id
        if (!this.customerId) {
            return;
        }

        // buffer customer id for later check
        const customerId = this.customerId;

        const skus: string[] = [];
        const entries: IProductEntry[] = [];
        this.productEntries.forEach(entry => {
            // consider only pending ones
            if (entry.status === EProductEntryStatus.pending) {
                // skip loaded
                if (this.loadedPrices.has(entry.product.id)) {
                    return;
                }
                // skip banners
                if ((entry.product.data as IProductDataV2)?.isBanner) {
                    entry.status = EProductEntryStatus.ready;
                    return;
                }

                // pass sku to load and update status
                const sku = entry.product.data?.sku;
                if (sku) {
                    this.skuMap.set(sku, entry.product.id);
                    skus.push(sku);
                    entries.push(entry);
                    entry.status = EProductEntryStatus.loading;
                }
            }
        });

        // skip if none needs loading
        if (skus.length === 0) {
            return;
        }
        // load prices
        const map = await this.loadSkus(skus);

        // do not process if customer id has changed
        if (!this.customerId || customerId !== this.customerId) {
            return;
        }

        if (map) {
            each(map, (mapEntry, sku: string) => {
                this.applySkuPrice(
                    sku,
                    Math.round(100 * mapEntry.NetPrice) / 100,
                    mapEntry.QuantityBreakInfos?.map(value => ({
                        customer_group: this.customerModel.data.group_id!,
                        price: value.NetPrice,
                        qty: value.Quantity
                    }))
                );
            });
        }

        // mark all entries that were not loaded as errored so we can display anything
        entries.forEach(entry => {
            if (entry.status === EProductEntryStatus.loading) {
                entry.status = EProductEntryStatus.error;
            }
            this.applyPrice(entry.product);
        });
    }, 100);

    private applySkuPrice(sku: string, price: number, tierPrices: ITierPrice[] | undefined) {
        const id = this.skuMap.get(sku);
        if (id !== undefined) {
            const entry = this.productEntries.get(id);
            if (entry && entry.status === EProductEntryStatus.loading) {
                entry.status = EProductEntryStatus.ready;
                this.loadedPrices.set(entry.product.id, { price, tierPrices });
                entry.resolver();
            }
        }
    }

    /**
     * Loads price information for current customerId and for requested skus
     */
    private async loadSkus(skus: string[]): Promise<IPricingResponse | undefined> {
        if (!this.customerId) {
            return;
        }

        const loadPartial = async (skus: string[]) => {
            return fetch(`/customerpricing?customer=${this.customerId}&quantity=${skus.length}&products=${skus.join(',')}`);
        };

        const result: IPricingResponse = {};
        await Promise.all(
            chunk(skus, 100).map(async (chunk: string[]) => {
                try {
                    // try to load all at once
                    const response = await loadPartial(chunk);

                    // if ok then pass as result
                    if (response.status === 200) {
                        Object.assign(result, await response.json());
                        return;
                    }

                    // if we get a failure there most likely one SKU is incorrect, lets load one by one
                    if (response.status >= 400 && response.status < 500 && skus.length > 0) {
                        const fallbackRes = await Promise.all(
                            skus.map(async sku => {
                                try {
                                    const skuRes = await loadPartial([sku]);
                                    const res = (await skuRes.json()) as IPricingResponse;
                                    return res;
                                } catch (err) {
                                    return {} as IPricingResponse;
                                }
                            })
                        );
                        Object.assign(result, ...fallbackRes);
                        return;
                    }

                    throw `Failed to load pricing data, status: ${response.status}`;
                } catch (err) {
                    this.loggerService.error(err);
                    return undefined;
                }
            })
        );

        return result;
    }

    /**
     * Gets either native pricing or custom for trade customer
     */
    private getProductPrice(product: Product, native: boolean): IProductEntryPrice | undefined {
        const data = product.data;
        if (!data) {
            return undefined;
        }

        const nativePrice = data._nativePrice;
        if (native) {
            if (nativePrice) {
                return nativePrice;
            }

            return {
                price: data.price!,
                orgPrice: data.org_price,
                groupPrice: data.group_price,
                groupOrgPrice: data.group_org_price,
                tierPrices: data.tier_price
            };
        }

        const loadedPriceData = this.customerId ? this.loadedPrices.get(product.id) : undefined;
        let loadedPrice = loadedPriceData?.price;
        const hasLoadedPrice = !!loadedPrice;

        if (loadedPrice) {
            const customerGroup = this.customerModel.data!.group_id;
            if (customerGroup) {
                const groupdPrice = data._nativePrice?.groupPrice?.[customerGroup];
                if (groupdPrice && groupdPrice < loadedPrice) {
                    loadedPrice = groupdPrice; // in case group price is lower
                }
            }
        }

        const price = data._nativePrice ? data._nativePrice.price : data.price!;
        const tierPrices = data._nativePrice ? data._nativePrice.tierPrices : data.tier_price;

        return {
            price: hasLoadedPrice && loadedPrice! < price ? loadedPrice! : price,
            orgPrice: !this.isTradeCustomer && data.was_price ? data.was_price : data.org_price,
            groupPrice: hasLoadedPrice ? undefined : data._nativePrice ? data._nativePrice.groupPrice : data.group_price,
            groupOrgPrice: hasLoadedPrice ? undefined : data._nativePrice ? data._nativePrice.groupOrgPrice : data.group_org_price,
            tierPrices: hasLoadedPrice
                ? loadedPriceData?.tierPrices?.map(tier => {
                      const tierPrice = tierPrices?.find(i => i.customer_group === tier.customer_group && i.qty === tier.qty)?.price;
                      return tierPrice && tierPrice < tier.price ? { ...tier, price: tierPrice } : tier;
                  })
                : tierPrices
        };
    }

    /**
     * Update prices in product data
     */
    private applyPrice(product: Product) {
        const data = product.data;
        if (data) {
            if (!data._nativePrice) {
                data._nativePrice = this.getProductPrice(product, true);
            }
            const entryPrice = this.getProductPrice(product, false);
            data.price = entryPrice!.price;
            data.org_price = entryPrice!.orgPrice;
            data.group_price = entryPrice!.groupPrice;
            data.group_org_price = entryPrice!.groupOrgPrice;
            data.tier_price = entryPrice!.tierPrices;
            const canDisplay = this.canDisplayPrice(product);

            const entry = this.productEntries.get(product.id);
            if (entry) {
                if (entry.canDisplay$.value !== canDisplay) {
                    entry.canDisplay$.next(canDisplay);
                }
            }
        }
    }

    /**
     * Cleanup
     */
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    /**
     * If product supports trade pricing
     */
    private supportsTradePrice(product: Product) {
        return !product.isConfigurable;
    }
}
